<template>
  <article
    id="article"
    ref="articleRef"
    class="flex flex-col h-full bg-white relative"
    :class="{
      'text-black': v1Templates,
      'text-dark': !v1Templates
    }"
  >
    <div
      id="article-wrapper"
      class="w-full mx-auto"
      :class="component.wrapper"
    >
      <div
        v-if="!!$slots.hero"
        id="article-hero"
        :class="component.hero"
      >
        <slot name="hero" />
      </div>

      <div
        id="article-header"
        :class="component.header"
      >
        <div
          id="article-header-inner"
          :class="layoutMaxWidth"
        >
          <AtomsBreadCrumb
            v-if="!v1Templates"
            :class="component.breadcrumbs"
            :pages="breadcrumbs"
            :has-json-schema="['Guides', 'Media'].includes(postType)"
          />

          <p
            v-if="!template.includes('Article3Column')"
            :class="component.title"
          >
            {{ title }}
          </p>

          <MoleculesArticleHeadDetails
            v-if="!v1Templates"
            :theme="
              postType === 'Expert Analysis'
                ? 'default'
                : 'v2'
            "
            :author="author"
            :tags="tags"
            :date="lastUpdate"
            :title="title"
            :has-share-buttons="hasShareButtons"
            :class="[
              `${template === 'ArticleWithSidebarV2' && subTitle
                ? 'mb-4 lg:mb-6'
                : ''}`
            ]"
            :fact-checked-collapse="template === 'Article3Column' && devices.breakpoint('sm', 'max')"
          />

          <p
            v-if="_hasSubtitle"
            :class="component.subtitle"
          >
            {{ subTitle }}
          </p>
        </div>
      </div>

      <main
        id="article-main-content"
        class="break-words"
        :class="[
          component.mainContent,
          layoutMaxWidth
        ]"
      >
        <div
          v-if="!template.includes('Article3Column')"
          ref="feature-image-wrapper"
          class="flex flex-col"
          :class="component.featuredImage.wrapper"
        >
          <figure
            v-if="!_isEmpty(featuredImage)"
            :class="component.featuredImage.inner"
          >
            <NuxtImg
              :src="`${featuredImage.url}?fm=webp`"
              :width="featuredImage.width"
              :height="featuredImage.height"
              :alt="title"
              :class="component.featuredImage.image"
              class="w-full"
              format="webp"
              preload
            />

            <figcaption
              v-if="template === 'ArticleLightWithSidebar' && !devices.isMobile.value"
              :class="component.subtitle"
              class="absolute bg-[#163c60]/80 bottom-0 py-3 px-6 hidden md:flex gap-4 items-center w-full"
            >
              <div class="flex-1">
                {{ subTitle }}
              </div>
            </figcaption>
          </figure>

          <div
            v-if="v1Templates"
            class="flex gap-8 justify-between text-sm font-bold"
            :class="{
              'pt-8': _isEmpty(featuredImage)
            }"
          >
            <p>
              by {{ author?.name }}
            </p>

            <time>
              Last update {{ dateToString(lastUpdate) }}
            </time>
          </div>

          <p
            v-else-if="featuredImage.description"
            class="mt-2 text text-gray-500 text-xs"
          >
            {{ featuredImage.description }}
          </p>
        </div>

        <div :class="component.mainContentInner">
          <slot name="main-content" />
        </div>

        <slot name="main-content-extra" />
      </main>

      <aside
        v-if="!!$slots['sidebar-left']"
        id="article-sidebar-left"
        ref="sidebarLeftRef"
        :class="component.sidebarLeft"
      >
        <div
          ref="sidebarLeftInnerRef"
          class="article-sidebar-left--inner"
        >
          <slot name="sidebar-left" />
        </div>
      </aside>

      <aside
        v-if="!!$slots['sidebar-right']"
        id="article-sidebar-right"
        ref="sidebarRightRef"
        :class="component.sidebarRight"
      >
        <div>
          <div
            ref="sidebarRightInnerRef"
            class="article-sidebar-right--inner flex flex-col gap-4 md:gap-5 ease-linear w-full"
            :class="[
              layoutMaxWidth,
              {
                'lg:w-[22rem]': template === 'ArticleWithSidebarV2',
                'lg:w-[16.75rem]': template === 'ArticleLightWithSidebar',
                'bg-white': template.includes('Article3Column')
              }
            ]"
          >
            <slot name="sidebar-right" />
            <slot name="sidebar-right-join-club-plus" />
          </div>

          <slot name="sidebar-right-extra" />
        </div>
      </aside>

      <div
        v-if="!!$slots['related-articles']"
        id="article-related-articles"
        :class="[
          component.relatedArticles,
          layoutMaxWidth,
          {
            'pb-10': !$slots.footer
          }
        ]"
      >
        <hr
          v-if="!!$slots['sidebar-right']"
          class="mb-10 border-b border-b-gray-400 border-t-0 border-l-0 border-r-0 lg:hidden"
        />
        <slot name="related-articles" />
      </div>

      <footer
        v-if="!!$slots.footer"
        id="article-footer"
        ref="articleFooterRef"
        class="col-start-1 col-span-full mt-10 empty:hidden"
      >
        <slot name="footer" />
      </footer>
    </div>
  </article>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import _isEmpty from 'underscore/cjs/isEmpty.js'

import { TEMPLATES } from '~/constants/pages/contentful.js'

defineOptions({
  name: 'TemplateContentfulContent'
})

const props = defineProps({
  // TODO: change to theme for consistency
  template: {
    type: String,
    default: null,
    validator: value => TEMPLATES.includes(value),
    required: true
  },

  title: {
    type: String,
    default: ''
  },

  post: {
    type: Object,
    default: null
  },

  postType: {
    type: String,
    default: 'Guides',
    validator: value => ['Guides', 'Articles', 'Expert Analysis', 'Media'].includes(value)
  },

  subTitle: {
    type: String,
    default: ''
  },

  featuredImage: {
    type: Object,
    default: () => ({})
  },

  lastUpdate: {
    type: String,
    default: ''
  },

  author: {
    type: [String, Object],
    default: () => ({})
  },

  notFound: {
    type: Boolean,
    default: false
  },

  vertical: {
    type: String,
    default: 'Health Insurance'
  },

  ctaText: {
    type: String,
    default: 'GET FREE QUOTES'
  },

  breadcrumbs: {
    type: Array,
    default: () => ([])
  },

  tags: {
    type: Array,
    default: () => ([])
  },

  slug: { // seo? meta?
    type: String,
    default: '/'
  },

  hasShareButtons: {
    type: Boolean,
    default: false
  },

  layoutMaxWidth: {
    type: String,
    default: ''
  }
})

const $slots = useSlots()
const { devices } = deviceChecker()

const layoutsStore = useLayoutsStore()
const { scrollPosition } = storeToRefs(layoutsStore)

const v1Templates = !['ArticleWithSidebarV2', 'Article3Column', 'Article3Column-D1', 'Article3Column-D2', 'Article3Column-D3'].includes(props.template)

const _hasSubtitle = computed(() => props.subTitle && props.template === 'ArticleWithSidebarV2')

const component = computed(() => {
  const template = props.template

  const wrapper = (() => {
    const grid = getKey(template, {
      default: 'grid grid-cols-contentful-sidebar-v2',
      ArticleLightWithSidebar: 'grid grid-cols-contentful-sidebar-light',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'flex flex-col px-4 md:px-0 md:grid md:grid-cols-contentful-3column-mb lg:grid-cols-contentful-3column-pc'
    })

    const layout = getKey(template, {
      default: '',
      ArticleLightWithSidebar: 'pt-8 lg:pt-14',
      ArticleWithSidebarV2: ['Expert Analysis', 'Media'].includes(props.postType)
        ? 'pt-6'
        : 'pt-5 md:pt-10',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'pt-6 md:pt-0'
    })

    return `${grid} ${layout}`
  })()

  const hero = (() => {
    const grid = getKey(template, {
      default: 'col-span-full',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 col-end-5 md:col-span-full'
    })

    const layout = getKey(template, {
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'mb-6'
    })

    return `row-start-1 ${grid} ${layout}`
  })()

  const header = (() => {
    const grid = getKey(template, {
      ArticleLightWithSidebar: 'row-start-1 col-start-2 col-end-4 lg:col-end-auto',
      ArticleWithSidebarV2: 'col-start-2 col-end-4 lg:col-end-auto',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 col-end-5 lg:col-start-3 lg:col-end-auto lg:row-start-2'
    })

    const margin = getKey(template, {
      ArticleLightWithSidebar: 'mb-7',
      ArticleWithSidebarV2: 'mb-5',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'mb-4 md:mb-6'
    })

    return removeClassExtras(`${grid} ${margin}`)
  })()

  const breadcrumbs = getKey(props.template, {
    default: 'mb-4 md:mb-5',
    'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'mb-4 md:mb-6'
  })

  const title = (() => {
    const text = getKey(props.template, {
      ArticleLightWithSidebar: 'text-xl lg:text-4.5xl font-bold',
      ArticleWithSidebarV2: 'text-2.5xl leading-10 font-bold md:text-4xl lg:text-4.5xl lg:leading-12'
    })

    const margin = !v1Templates || _hasSubtitle.value 
      ? getKey(props.template, {
        ArticleLightWithSidebar: 'mb-7',
        ArticleWithSidebarV2: 'mb-2 lg:mb-4'
      })
      : ''

    return `leading-tight ${text} ${margin}`
  })()

  const subtitle = getKey(template, {
    ArticleLightWithSidebar: 'text-white font-bold text-lg',
    ArticleWithSidebarV2: 'text-base lg:text-lg leading-tight'
  })

  const featuredImage = {
    wrapper: getKey(template, {
      ArticleLightWithSidebar: 'gap-y-6',
      ArticleWithSidebarV2: 'mb-4 md:mb-10'
    }),
    inner: getKey(template, {
      ArticleLightWithSidebar: 'relative lg:shadow-md rounded-sm'
    }),
    image: getKey(template, {
      ArticleWithSidebarV2: `w-full object-cover rounded-2.5xl ${getKey(props.postType, {
        default: 'h-[11.0625rem] md:h-[14.5625rem] lg:h-[340px]',
        'Expert Analysis': 'h-[177px] md:h-auto'
      })}`
    })
  }

  const mainContent = (() => {
    const grid = getKey(template, {
      default: 'col-start-2 col-span-2',
      ArticleLightWithSidebar: 'col-start-2 col-end-4 lg:col-end-auto',
      ArticleWithSidebarV2: 'col-start-2 col-end-4 lg:col-end-auto',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'max-w-full md:max-w-none md:col-start-2 md:col-end-5 lg:col-start-3 lg:col-end-auto lg:row-start-3'
    })

    return `${grid}`
  })()

  const mainContentInner = (() => {
    const className = (() => {
      if (props.template === 'Article3Column') {
        return 'article-3column-content'
      }

      return v1Templates
        ? 'post-content'
        : 'article-content'
    })()

    const padding = getKey(props.template, {
      default: 'pb-7',
      ArticleLightWithSidebar: 'lg:pb-12',
      ArticleWithSidebarV2: 'pb-10 lg:pb-0',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'pb-6 lg:pb-10'
    })

    return removeClassExtras(`${className} ${padding}`)
  })()

  const sidebarLeft = (() => {
    const grid = getKey(template, {
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 row-start-2 row-span-3'
    })

    const margin = getKey(template, {
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'lg:pr-9 lg:pt-[43px]'
    })

    return `${grid} ${margin}`
  })()

  const sidebarRight = (() => {
    const grid = getKey(template, {
      default: 'row-start-3 col-start-1 col-span-full lg:row-start-1 lg:row-span-3 lg:col-start-3 lg:col-end-4',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-4 row-start-2 row-span-3'
    })

    const layout = getKey(template, {
      ArticleLightWithSidebar: 'pb-8 lg:pb-14',
      ArticleWithSidebarV2: ['Expert Analysis', 'Media'].includes(props.postType)
        ? 'mb-10 lg:mb-0 lg:pt-16'
        : 'pb-[4.1875rem] lg:pt-10'
    })

    const margin = getKey(template, {
      default: 'px-4 lg:pl-8 lg:pr-0',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'lg:pl-9 lg:pt-[2.6875rem]'
    })

    return removeClassExtras(`relative ${margin} ${grid} ${layout}`)
  })()

  const relatedArticles = getKey(template, {
    default: 'col-start-2 col-span-2 lg:col-end-2',
    ArticleWithSidebarV2: 'col-start-2 col-span-2 lg:col-end-2 lg:pt-10',
    'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 col-end-5 lg:col-start-3 lg:col-end-auto'
  })

  return {
    wrapper,
    hero,
    header,
    breadcrumbs,
    title,
    subtitle,
    featuredImage,
    mainContent,
    mainContentInner,
    sidebarLeft,
    sidebarRight,
    relatedArticles
  }
})

function dateToString (str) {
  if (!str) {
    return ''
  }

  let date = new Date(str)
  date = `${date.getDate()} ${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`

  return date
}

const sidebarInnerBaseOffset = getKey(props.template, {
  ArticleWithSidebarV2: 144,
  'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 391.66668701171875,
  ArticleLightWithSidebar: 120
})

const sidebarRightRef = ref()
const sidebarRightInnerRef = ref()

const sidebarLeftRef = ref()
const sidebarLeftInnerRef = ref()

const sidebarSticky = ref({
  right: false,
  left: false
})
const stickToBottom = ref({
  right: false,
  left: false
})

watch(scrollPosition, val => devices.breakpoint('lg') && handleScroll(val))

watch(devices.width, () => {
  if (!devices.breakpoint('lg')) {
    if (sidebarRightInnerRef.value) {
      sidebarRightInnerRef.value.style.position = 'relative'
    }

    if (sidebarLeftInnerRef.value) {
      sidebarLeftInnerRef.value.style.position = 'relative'
    }
  }
})

function handleScroll (scrollPos) {
  if (props.postType === 'Expert Analysis' || props.template.includes('Article3Column')) {
    return
  }

  const sidebarRight = sidebarRightRef.value && sidebarRightInnerRef.value
    ? {
      main: sidebarRightRef.value,
      inner: sidebarRightInnerRef.value
    }
    : null

  const sidebarLeft = sidebarLeftRef.value && sidebarLeftInnerRef.value
    ? {
      main: sidebarLeftRef.value,
      inner: sidebarLeftInnerRef.value
    }
    : null

  const sidebars = {
    right: sidebarRight,
    left: sidebarLeft
  }
  const sidebarKeys = Object.keys(sidebars)

  if (_isEmpty(sidebars.right) && _isEmpty(sidebars.left)) {
    return
  }

  for (let i = 0; i < sidebarKeys.length; i++) {
    const key = sidebarKeys[i]
    const sidebar = sidebars?.[key]

    if (_isEmpty(sidebar)) {
      continue
    }

    const mainRect = sidebar?.main.getBoundingClientRect()
    const innerRect = sidebar?.inner.getBoundingClientRect()

    const sidebarHeight = mainRect?.height || 0
    const innerHeight = innerRect?.height || 0

    const newOffset = scrollPos + innerHeight

    sidebarSticky.value[key] = scrollPos >= sidebarInnerBaseOffset
    stickToBottom.value[key] = newOffset >= (sidebarHeight + 40)

    if (
      (sidebarSticky.value.left && stickToBottom.value.left) ||
      (sidebarSticky.value.right && stickToBottom.value.right)
    ) {
      if (sidebars?.left) {
        sidebars.left.inner.style.position = 'absolute'

        sidebars.left.inner.style.top = ''
        sidebars.left.inner.style.paddingTop = ''
        sidebars.left.inner.style.bottom = '25px'
      }

      if (sidebars?.right) {
        sidebars.right.inner.style.position = 'absolute'

        sidebars.right.inner.style.top = ''
        sidebars.right.inner.style.paddingTop = ''
        sidebars.right.inner.style.bottom = '25px'
      }
    } else if (sidebarSticky.value[key] && !stickToBottom.value[key]) {
      sidebar.inner.style.position = 'fixed'

      sidebar.inner.style.top = '0'
      sidebar.inner.style.paddingTop = '10px'
      sidebar.inner.style.bottom = ''
    } else if (!sidebarSticky.value[key]) {
      sidebar.inner.style.position = 'relative'

      sidebar.inner.style.top = ''
      sidebar.inner.style.paddingTop = ''
      sidebar.inner.style.bottom = ''
    }
  }
}
</script>

<style
  lang="scss"
  src="./index.scss"
>
</style>
